import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { ScrollArea } from "./ui/scroll-area";
import {
  MapPin,
  DollarSign,
  TrendingUp,
  Building2,
  Home,
  Calendar,
  FileText,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  BedDouble,
  Bath,
  Ruler,
  LandPlot,
  Pencil,
  Camera,
  Film,
  Paperclip,
  ExternalLink,
} from "lucide-react";
import { useEffect, useState } from "react";
import { fetchViewUrl } from "../lib/s3Upload";

const STATUS_CONFIG = {
  draft: {
    label: "Draft",
    color: "bg-amber-500/10 text-amber-600 border-amber-200",
    icon: FileText,
  },
  pending: {
    label: "Pending Review",
    color: "bg-orange-500/10 text-orange-600 border-orange-200",
    icon: Clock,
  },
  active: {
    label: "Live",
    color: "bg-green-500/10 text-green-600 border-green-200",
    icon: CheckCircle,
  },
  approved: {
    label: "Approved",
    color: "bg-blue-500/10 text-blue-600 border-blue-200",
    icon: CheckCircle,
  },
  reserved: {
    label: "Reserved",
    color: "bg-purple-500/10 text-purple-600 border-purple-200",
    icon: Building2,
  },
  in_closing: {
    label: "In Closing",
    color: "bg-indigo-500/10 text-indigo-600 border-indigo-200",
    icon: FileText,
  },
  closed: {
    label: "Closed",
    color: "bg-gray-500/10 text-gray-600 border-gray-200",
    icon: CheckCircle,
  },
  rejected: {
    label: "Rejected",
    color: "bg-red-500/10 text-red-600 border-red-200",
    icon: XCircle,
  },
  changes_requested: {
    label: "Changes Requested",
    color: "bg-yellow-500/10 text-yellow-700 border-yellow-300",
    icon: AlertTriangle,
  },
};

const PROPERTY_LABELS = {
  single_family: "Single Family",
  multi_family: "Multi-Family",
  apartment: "Apartment",
  commercial: "Commercial",
  land: "Land",
  mixed_use: "Mixed Use",
};

const DEAL_LABELS = {
  flip: "Flip",
  rental: "Rental",
  development: "Development",
};

const AgentDealDetailModal = ({ open, onOpenChange, deal, onEdit }) => {
  // Hydrate view URLs for any existing media (so we can render <a> tags)
  // Shape: { photos: {kitchen: signedUrl, ...}, videos: {...}, documents: {...} }
  const [mediaUrls, setMediaUrls] = useState({
    photos: {},
    videos: {},
    documents: {},
  });

  useEffect(() => {
    if (!deal || !open) return;
    let cancelled = false;

    const hydrate = async (mediaMap) => {
      const urls = {};
      await Promise.all(
        Object.entries(mediaMap || {}).map(async ([key, meta]) => {
          if (!meta?.storage_path) return;
          try {
            urls[key] = await fetchViewUrl(meta.storage_path);
          } catch (_) {
            // leave undefined — UI will render filename without link
          }
        }),
      );
      return urls;
    };

    (async () => {
      const [photos, videos, documents] = await Promise.all([
        hydrate(deal.photos),
        hydrate(deal.videos),
        hydrate(deal.documents),
      ]);
      if (!cancelled) setMediaUrls({ photos, videos, documents });
    })();

    return () => {
      cancelled = true;
    };
  }, [deal, open]);

  if (!deal) return null;

  const status = STATUS_CONFIG[deal.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;

  const formatCurrency = (v) => {
    if (!v) return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const roi =
    deal.estimated_arv && deal.asking_price
      ? (
          ((deal.estimated_arv -
            deal.asking_price -
            (deal.estimated_rehab_cost || 0)) /
            deal.asking_price) *
          100
        ).toFixed(1)
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-hidden p-0"
        data-testid="agent-deal-detail-modal"
      >
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Building2 className="w-5 h-5" />
            Deal Details
          </DialogTitle>
          <DialogDescription className="sr-only">
            Full details for the selected deal, including property info,
            financials, notes, and uploaded media.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh] px-6">
          <div className="space-y-5 pb-6">
            {/* Status + Admin Feedback — shown prominently at top when changes_requested or rejected */}
            {(deal.status === "changes_requested" ||
              deal.status === "rejected") &&
              deal.admin_notes && (
                <div
                  className={`p-4 rounded-lg border-l-4 ${deal.status === "changes_requested" ? "bg-yellow-50 border-yellow-400" : "bg-red-50 border-red-400"}`}
                  data-testid="admin-feedback-section"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare
                      className={`w-4 h-4 ${deal.status === "changes_requested" ? "text-yellow-600" : "text-red-600"}`}
                    />
                    <span
                      className={`text-sm font-semibold ${deal.status === "changes_requested" ? "text-yellow-700" : "text-red-700"}`}
                    >
                      {deal.status === "changes_requested"
                        ? "Changes Requested by Admin"
                        : "Rejection Reason"}
                    </span>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed">
                    {deal.admin_notes}
                  </p>
                  {deal.reviewed_at && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {deal.reviewed_by_name &&
                        `By ${deal.reviewed_by_name} · `}
                      {formatDate(deal.reviewed_at)}
                    </p>
                  )}
                </div>
              )}

            {/* Status Badge */}
            <div className="flex items-center justify-between">
              <Badge
                variant="outline"
                className={`${status.color} px-3 py-1 text-sm gap-1.5`}
              >
                <StatusIcon className="w-3.5 h-3.5" />
                {status.label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Submitted {formatDate(deal.created_at)}
              </span>
            </div>

            {/* Admin notes for approved deals */}
            {deal.admin_notes &&
              deal.status !== "changes_requested" &&
              deal.status !== "rejected" && (
                <div
                  className="p-3 rounded-lg bg-blue-50 border border-blue-100"
                  data-testid="admin-notes-section"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare className="w-3.5 h-3.5 text-blue-600" />
                    <span className="text-xs font-semibold text-blue-700">
                      Admin Notes
                    </span>
                  </div>
                  <p className="text-sm text-foreground">{deal.admin_notes}</p>
                </div>
              )}

            <Separator />

            {/* Property Info */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Property
              </h3>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">
                      {deal.property_address}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {deal.city}, {deal.state} {deal.zip_code}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground ml-7">
                  <span>
                    {PROPERTY_LABELS[deal.property_type] || deal.property_type}
                  </span>
                  {deal.deal_type && (
                    <span>
                      · {DEAL_LABELS[deal.deal_type] || deal.deal_type}
                    </span>
                  )}
                </div>
                {(deal.bedrooms ||
                  deal.bathrooms ||
                  deal.square_footage ||
                  deal.lot_size) && (
                  <div className="flex flex-wrap gap-3 ml-7 mt-1">
                    {deal.bedrooms && (
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <BedDouble className="w-3.5 h-3.5" /> {deal.bedrooms}{" "}
                        bed
                      </span>
                    )}
                    {deal.bathrooms && (
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Bath className="w-3.5 h-3.5" /> {deal.bathrooms} bath
                      </span>
                    )}
                    {deal.square_footage && (
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Ruler className="w-3.5 h-3.5" />{" "}
                        {deal.square_footage.toLocaleString()} sqft
                      </span>
                    )}
                    {deal.lot_size && (
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <LandPlot className="w-3.5 h-3.5" /> {deal.lot_size}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Financials */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Financials
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                    <DollarSign className="w-3 h-3" /> Selling Price
                  </div>
                  <p className="font-semibold text-foreground">
                    {formatCurrency(deal.asking_price)}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                    <TrendingUp className="w-3 h-3" /> ARV
                  </div>
                  <p className="font-semibold text-green-600">
                    {formatCurrency(deal.estimated_arv)}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                    Rehab Cost
                  </div>
                  <p className="font-semibold text-foreground">
                    {formatCurrency(deal.estimated_rehab_cost)}
                  </p>
                </div>
                {deal.wholesale_fee > 0 && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      Wholesale Fee
                    </div>
                    <p className="font-semibold text-foreground">
                      {formatCurrency(deal.wholesale_fee)}
                    </p>
                  </div>
                )}
                {roi && (
                  <div className="p-3 rounded-lg bg-green-50 col-span-2">
                    <div className="flex items-center gap-1.5 text-xs text-green-700 mb-1">
                      <TrendingUp className="w-3 h-3" /> Estimated ROI
                    </div>
                    <p className="font-semibold text-green-700">{roi}%</p>
                  </div>
                )}
              </div>
            </div>

            {/* Scope of Work */}
            {(deal.repair_notes || deal.additional_notes) && (
              <>
                <Separator />
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Notes
                  </h3>
                  {deal.repair_notes && (
                    <div className="mb-3">
                      <p className="text-xs text-muted-foreground mb-1">
                        Repair / Scope of Work
                      </p>
                      <p className="text-sm text-foreground">
                        {deal.repair_notes}
                      </p>
                    </div>
                  )}
                  {deal.additional_notes && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        Additional Notes
                      </p>
                      <p className="text-sm text-foreground">
                        {deal.additional_notes}
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Media — photos, videos, documents */}
            {(() => {
              const photoEntries = Object.entries(deal.photos || {});
              const videoEntries = Object.entries(deal.videos || {});
              const docEntries = Object.entries(deal.documents || {});
              const hasAny =
                photoEntries.length || videoEntries.length || docEntries.length;
              if (!hasAny) return null;

              const labelize = (k) =>
                k
                  .split("_")
                  .map((w) => w[0]?.toUpperCase() + w.slice(1))
                  .join(" ");

              return (
                <>
                  <Separator />
                  <div data-testid="deal-media-section">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                      Uploaded Media
                    </h3>

                    {photoEntries.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                          <Camera className="w-3.5 h-3.5" />
                          Photos ({photoEntries.length})
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {photoEntries.map(([key, meta]) => {
                            const url = mediaUrls.photos[key];
                            return (
                              <a
                                key={key}
                                href={url || "#"}
                                target={url ? "_blank" : undefined}
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  if (!url) e.preventDefault();
                                }}
                                className="group relative block aspect-square rounded-md border border-border overflow-hidden bg-muted hover:ring-2 hover:ring-blue-500/40 transition"
                                title={meta.filename || labelize(key)}
                                data-testid={`deal-photo-${key}`}
                              >
                                {url ? (
                                  <img
                                    src={url}
                                    alt={meta.filename || key}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Camera className="w-5 h-5 text-muted-foreground" />
                                  </div>
                                )}
                                <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] py-0.5 px-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                                  {labelize(key)}
                                </div>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {videoEntries.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                          <Film className="w-3.5 h-3.5" />
                          Videos ({videoEntries.length})
                        </div>
                        <div className="space-y-1">
                          {videoEntries.map(([key, meta]) => {
                            const url = mediaUrls.videos[key];
                            return (
                              <a
                                key={key}
                                href={url || "#"}
                                target={url ? "_blank" : undefined}
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  if (!url) e.preventDefault();
                                }}
                                className={`flex items-center justify-between gap-2 p-2 rounded-md border border-border bg-muted/30 ${url ? "hover:bg-muted hover:border-blue-500/40" : "opacity-70 cursor-not-allowed"} transition`}
                                data-testid={`deal-video-${key}`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <Film className="w-4 h-4 text-blue-500 shrink-0" />
                                  <span className="text-sm truncate">
                                    {meta.filename || labelize(key)}
                                  </span>
                                </div>
                                {url && (
                                  <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                )}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {docEntries.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                          <Paperclip className="w-3.5 h-3.5" />
                          Documents ({docEntries.length})
                        </div>
                        <div className="space-y-1">
                          {docEntries.map(([key, meta]) => {
                            const url = mediaUrls.documents[key];
                            return (
                              <a
                                key={key}
                                href={url || "#"}
                                target={url ? "_blank" : undefined}
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  if (!url) e.preventDefault();
                                }}
                                className={`flex items-center justify-between gap-2 p-2 rounded-md border border-border bg-muted/30 ${url ? "hover:bg-muted hover:border-blue-500/40" : "opacity-70 cursor-not-allowed"} transition`}
                                data-testid={`deal-doc-${key}`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                                  <span className="text-sm truncate">
                                    {meta.filename || labelize(key)}
                                  </span>
                                </div>
                                {url && (
                                  <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                )}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}

            {/* Reservation info */}
            {deal.reserved_by_name && (
              <>
                <Separator />
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Reservation
                  </h3>
                  <p className="text-sm text-foreground">
                    Reserved by{" "}
                    <span className="font-medium">{deal.reserved_by_name}</span>
                    {deal.reserved_at && (
                      <span className="text-muted-foreground">
                        {" "}
                        on {formatDate(deal.reserved_at)}
                      </span>
                    )}
                  </p>
                </div>
              </>
            )}
          </div>
        </ScrollArea>

        {/* Edit button for editable statuses */}
        {["changes_requested", "rejected", "pending", "draft"].includes(
          deal.status,
        ) &&
          onEdit && (
            <DialogFooter className="p-6 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button
                className="bg-blue-500 hover:bg-blue-600"
                onClick={() => {
                  onOpenChange(false);
                  onEdit(deal);
                }}
                data-testid="edit-resubmit-btn"
              >
                <Pencil className="w-4 h-4 mr-2" />
                {deal.status === "changes_requested"
                  ? "Edit & Resubmit"
                  : "Edit Deal"}
              </Button>
            </DialogFooter>
          )}
      </DialogContent>
    </Dialog>
  );
};

export default AgentDealDetailModal;
